import React, { Fragment, useCallback } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { MeWithIdentitiesAndSubscribed } from '../../../../graphql/queries/account';
import { getPurchasedIdentitiesOnFolder } from '../../../../util/account';
import { AlbumFolderWithIdentityAccess } from '../../../../graphql/queries/album-folder';
import { onSelectIdentity } from '../../../../actions/discover-album-folder';
import useOpenModal from '../../../../hooks/useOpenModal';
import { CREATE_ACCOUNT_IDENTITY_MODAL } from '../../../../constants/modals';
import { ALBUM_IDENTITY_ACCESS } from '../../../../constants/identity';
import {
  getAccountIdentityNamesArray,
  identitiesFilteredOnAccess,
  sortByIdentityRelationship,
} from '../../../../util/identity';
import AlbumFolderPurchaseCreateAccountIdentityContainer, {
  AlbumFolderPurchaseCreateAccountIdentityComponentContainer,
} from '../../../../containers/purchase/AlbumFolderPurchaseCreateAccountIdentityContainer';
import OnboardingLayoutScrollableContent from '../../../../apps/temp/views/onboarding/OnboardingLayout/components/OnboardingLayoutScrollableContent';
import { getAsset } from '../../../../util/assets';
import ContextualString from '../../../../components/contextual/ContextualString';
import PlusCircleIcon from '../../../../components/icons/PlusCircleIcon';
import UpdateAccountIdentityContainer from '../../../../apps/consumer/containers/UpdateAccountIdentityContainer';
import JoinAlbumFolderIdentity from './JoinAlbumFolderIdentity';

const propTypes = {
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  identities: PropTypes.arrayOf(PropTypes.object),
  purchasedIdentities: PropTypes.arrayOf(PropTypes.object),
  subscribedAlbumFolderIdentities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      album_folder: PropTypes.shape({
        id: PropTypes.string,
      }),
      identity: PropTypes.shape({
        id: PropTypes.string,
      }),
    }),
  ),
};

const JoinAlbumFolderIdentitiesSelection = ({
  identities,
  albumFolder,
  purchasedIdentities,
  subscribedAlbumFolderIdentities,
}) => {
  const { selectedIdentities } = useSelector(({ discoverAlbumFolder }) => ({
    selectedIdentities: discoverAlbumFolder.identities,
  }));
  const dispatch = useDispatch();
  const openModal = useOpenModal();

  const onSelectIdentityCallback = useCallback(
    (...args) => dispatch(onSelectIdentity(...args)),
    [dispatch],
  );

  const onAddFace = event => {
    if (event) {
      event.preventDefault();
    }

    openModal(CREATE_ACCOUNT_IDENTITY_MODAL);
  };

  const onIdentityUpdatedOrCreated = identity => {
    onSelectIdentityCallback({
      ...identity,
      albumFolders: [albumFolder],
      selectedAlbums: [],
    });
  };

  const renderIdentity = identity => {
    const isSelected = !!(selectedIdentities || []).find(
      selectedIdentity => selectedIdentity.id === identity.id,
    );
    const isPurchased = !!(purchasedIdentities || []).find(
      purchasedIdentity => purchasedIdentity.id === identity.id,
    );
    const isJoined = !!(subscribedAlbumFolderIdentities || []).find(
      subscribedItem => subscribedItem.identity.id === identity.id,
    );

    return (
      <JoinAlbumFolderIdentity
        key={identity.id}
        identity={identity}
        isSelected={isSelected}
        isPurchased={isPurchased}
        albumFolder={albumFolder}
        isJoined={isJoined}
      />
    );
  };

  const sortedIdentities = sortByIdentityRelationship(identities ?? []);
  const filteredIdentities = albumFolder
    ? identitiesFilteredOnAccess(
        sortedIdentities,
        albumFolder.album_identity_access,
      )
    : [];

  return (
    <Fragment>
      {filteredIdentities?.length === 0 ? (
        <div className="w-100">
          <AlbumFolderPurchaseCreateAccountIdentityComponentContainer
            includeTitle={false}
            onIdentityCreated={onIdentityUpdatedOrCreated}
            stringContext={albumFolder}
            identityNames={getAccountIdentityNamesArray(sortedIdentities)}
            requiresSelfieChecklistVerification
            noCancelOption
          />
        </div>
      ) : (
        <OnboardingLayoutScrollableContent className="w-100">
          <div className="identity-list-container">
            {(filteredIdentities || []).map(identityItem =>
              renderIdentity(identityItem),
            )}

            {albumFolder?.album_identity_access !==
              ALBUM_IDENTITY_ACCESS.PRIMARY_ONLY && (
              <button onClick={onAddFace} className="new-identity-container">
                <img
                  src={getAsset('/icon-add-face-blue.svg')}
                  alt=""
                  className="mr-2"
                  width="42"
                  height="42"
                />
                <div className="mb-0">
                  <ContextualString
                    id="consumerAddNewIdentity"
                    subtype={albumFolder.album_subtype}
                    defaultMessage="Find photos of a family member"
                  />
                  <PlusCircleIcon
                    color="#0d84fe"
                    className="ml-1"
                    style={{ verticalAlign: 'middle' }}
                  />
                </div>
              </button>
            )}
          </div>
        </OnboardingLayoutScrollableContent>
      )}

      <AlbumFolderPurchaseCreateAccountIdentityContainer
        onIdentityCreated={onIdentityUpdatedOrCreated}
        stringContext={albumFolder}
        identityNames={getAccountIdentityNamesArray(sortedIdentities)}
        requiresSelfieChecklistVerification
      />
      <UpdateAccountIdentityContainer
        requiresSelfieChecklistVerification
        stringContext={albumFolder}
        onIdentityUpdated={onIdentityUpdatedOrCreated}
      />
    </Fragment>
  );
};

JoinAlbumFolderIdentitiesSelection.propTypes = propTypes;

const withUserData = graphql(MeWithIdentitiesAndSubscribed, {
  options: ({ match }) => ({
    variables: {
      albumFolderIds: [match.params.albumFolderId],
    },
  }),
  props: ({ data: { me, loading } = {}, ownProps: { albumFolder } }) => ({
    loading,
    accountId: me?.id,
    identities: me?.linked_identities?.records,
    purchasedIdentities: getPurchasedIdentitiesOnFolder(
      me,
      albumFolder?.root?.id,
    ),
    subscribedAlbumFolderIdentities:
      me?.subscribed_album_folder_identities?.records ?? [],
  }),
});

const withAlbumFolder = graphql(AlbumFolderWithIdentityAccess, {
  options: ({ match }) => ({
    variables: {
      id: match.params.albumFolderId,
    },
    notifyOnNetworkStatusChange: true,
  }),
  props: ({ data: { albumFolder, loading } = {} }) => ({
    albumFolder,
    loading,
  }),
});

export default compose(
  withRouter,
  withAlbumFolder,
  withUserData,
)(JoinAlbumFolderIdentitiesSelection);
